import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/redux/store'
import WebSocketManager from '@/services/websocketManager'
import { setVoteCast } from '@/redux/slices/teamDecisionSlice'

const wsManager = WebSocketManager.getInstance()

export const proposeStrategy = createAsyncThunk<
  void,
  { strategyText: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'teamDecision/proposeStrategy',
  async ({ strategyText }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('team_chat', {
      type: 'PROPOSE_STRATEGY',
      strategy_text: strategyText,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const loadProposals = createAsyncThunk<
  void,
  { gameRoundId?: number } | void,
  { dispatch: AppDispatch; state: RootState }
>(
  'teamDecision/loadProposals',
  async (params, { getState, rejectWithValue }) => {
    const payload: Record<string, unknown> = { type: 'LOAD_PROPOSALS' }
    const currentRoundId = getState().timerConfiguration.status?.roundData?.id
    const requestedRoundId =
      params &&
      typeof params === 'object' &&
      'gameRoundId' in params &&
      params.gameRoundId
        ? params.gameRoundId
        : currentRoundId
    if (requestedRoundId) {
      payload.game_round_id = requestedRoundId
    }

    const success = wsManager.sendMessage('team_chat', payload)

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const castVote = createAsyncThunk<
  void,
  { proposalId: number },
  { dispatch: AppDispatch; state: RootState }
>(
  'teamDecision/castVote',
  async ({ proposalId }, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const currentVoteCounts = state.teamDecision.voteCounts
    const previousVoteProposalId = state.teamDecision.myVoteProposalId

    const optimisticVoteCounts: Record<number, number> = { ...currentVoteCounts }
    if (previousVoteProposalId && previousVoteProposalId !== proposalId) {
      optimisticVoteCounts[previousVoteProposalId] = Math.max(
        0,
        (optimisticVoteCounts[previousVoteProposalId] || 0) - 1
      )
    }
    optimisticVoteCounts[proposalId] = (optimisticVoteCounts[proposalId] || 0) + 1

    const success = wsManager.sendMessage('team_chat', {
      type: 'CAST_VOTE',
      proposal_id: proposalId,
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }

    dispatch(
      setVoteCast({
        proposalId,
        voteCounts: optimisticVoteCounts,
        myVoteProposalId: proposalId,
      })
    )

  }
)

export const closeVoting = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  'teamDecision/closeVoting',
  async (_, { getState, rejectWithValue }) => {
    const success = wsManager.sendMessage('team_chat', {
      type: 'CLOSE_VOTING',
    })

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }

    const currentRoundId = getState().timerConfiguration.status?.roundData?.id
    const payload: Record<string, unknown> = { type: 'LOAD_PROPOSALS' }
    if (currentRoundId) {
      payload.game_round_id = currentRoundId
    }
    wsManager.sendMessage('team_chat', payload)
  }
)
